import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  FaPlay,
  FaPause,
  FaVolumeMute,
  FaVolumeUp,
  FaExpand,
  FaForward,
  FaArrowLeft,
  FaArrowRight
} from "react-icons/fa";

import hero1280Asset from "../Assets/optimized/hero-1280.mp4.asset.json";
import hero1920Asset from "../Assets/optimized/hero-1920.mp4.asset.json";
import { assetUrl } from "../Assets/assetUrl";
import { heroPosterUrl } from "../Assets/heroImages";

// Compressed WebP/JPEG poster (was a 2.3 MB PNG).
const heroPoster = heroPosterUrl;

// The mp4s are CDN-hosted; resolve them so they also load on a local dev server.
const HERO_1280 = assetUrl(hero1280Asset);
const HERO_1920 = assetUrl(hero1920Asset);

/**
 * Because the hero uses object-fit: cover, the source is always cropped and
 * upscaled to the viewport. Serving a 720p master on a wide/retina screen
 * looks soft, so pick the 1080p high-bitrate encode whenever the rendered
 * box (CSS width x DPR) needs more than ~1280 device pixels.
 */
const pickHeroSource = () => {
  if (typeof window === "undefined") return HERO_1920;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const devicePixels = window.innerWidth * dpr;

  // Respect data-saver / slow connections – stay on the lighter encode.
  const conn = navigator.connection;
  if (conn && (conn.saveData || /^(slow-)?2g$/.test(conn.effectiveType || ""))) {
    return HERO_1280;
  }

  return devicePixels > 1280 ? HERO_1920 : HERO_1280;
};

export default function HeroVideo({ onVideoEnd, onNext, onPrev }) {
  const videoRef = useRef(null);

  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );
  // Chosen once on mount so switching sources never restarts playback.
  const [heroVideo] = useState(pickHeroSource);

  // Latest callback in a ref so the watchdog below never goes stale.
  const onVideoEndRef = useRef(onVideoEnd);
  useEffect(() => {
    onVideoEndRef.current = onVideoEnd;
  }, [onVideoEnd]);

  // Watchdog: on mobile the mp4 can stall on the poster frame (autoplay
  // blocked, frozen network, data-saver) without ever firing `ended` or
  // `error` — which would leave the hero static forever. If playback hasn't
  // started within 8s, hand over to the rotating slider instead.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    // Mobile browsers reject autoplay unless the element is muted + inline;
    // both are set, but the promise can still be rejected on first paint, so
    // retry explicitly here instead of skipping the video entirely.
    video.muted = true;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => {
        const retry = () => {
          const again = video.play();
          if (again && typeof again.catch === "function") again.catch(() => {});
        };
        document.addEventListener("touchstart", retry, { once: true });
        document.addEventListener("click", retry, { once: true });
      });
    }

    // Stall watchdog: if playback never starts, hand over to the slider so the
    // reel keeps looping instead of freezing on the poster.
    const timer = setTimeout(() => {
      if (video.currentTime < 0.1 && !video.ended) {
        onVideoEndRef.current?.();
      }
    }, 8000);

    const cancel = () => clearTimeout(timer);
    video.addEventListener("timeupdate", cancel, { once: true });
    video.addEventListener("ended", cancel, { once: true });

    return () => {
      clearTimeout(timer);
      video.removeEventListener("timeupdate", cancel);
      video.removeEventListener("ended", cancel);
    };
  }, [isMobile]);



  // Keyboard parity with the slider: arrow keys move through the reel even
  // while the intro video is the active frame.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" && onNext) onNext();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onNext, onPrev]);

  // Play / Pause
  const togglePlay = () => {
    const video = videoRef.current;

    if (!video) return;

    if (video.paused) {
      // Autoplay policies can reject play(); swallow the rejection so it never
      // surfaces as an unhandled runtime error.
      const attempt = video.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(() => setPlaying(false));
      }
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  // Mute / Unmute
  const toggleMute = () => {
    const video = videoRef.current;

    if (!video) return;

    video.muted = !video.muted;
    setMuted(video.muted);
  };

  // Update Progress
  const handleProgress = () => {
    const video = videoRef.current;

    if (!video) return;

    const { currentTime, duration } = video;

    // Live/streaming or not-yet-loaded media reports NaN or Infinity.
    if (!Number.isFinite(duration) || duration <= 0) return;
    if (!Number.isFinite(currentTime) || currentTime < 0) return;

    const percentage = Math.min(100, Math.max(0, (currentTime / duration) * 100));

    setProgress(percentage);
  };

  // Skip Video
  const skipVideo = () => {
    const video = videoRef.current;

    // Nothing to seek — still hand control to the slider.
    if (!video) {
      if (onVideoEnd) onVideoEnd();
      return;
    }

    const duration = Number(video.duration);
    const seekable = video.seekable;

    // Prefer the last seekable range end; fall back to duration. Both can be
    // NaN/Infinity depending on readyState and autoplay/preload state.
    let target = NaN;

    try {
      if (seekable && seekable.length > 0) {
        target = Number(seekable.end(seekable.length - 1));
      }
    } catch {
      /* seekable can throw on some browsers before metadata loads */
    }

    if (!Number.isFinite(target) || target <= 0) {
      target = duration;
    }

    if (Number.isFinite(target) && target > 0) {
      // Nudge just under the end: exactly `duration` is rejected by some
      // engines and can re-fire `ended` handlers.
      const clamped = Math.min(Math.max(target - 0.05, 0), target);

      if (Number.isFinite(clamped)) {
        try {
          video.currentTime = clamped;
        } catch {
          /* seeking not permitted yet — safe to ignore */
        }
      }
    }

    try {
      video.pause();
    } catch {
      /* no-op */
    }

    setPlaying(false);
    setProgress(100);

    if (onVideoEnd) {
      onVideoEnd();
    }
  };

  // If the mp4 cannot load at all, don't leave a blank hero — move on.
  const handleError = () => {
    if (onVideoEnd) onVideoEnd();
  };


  // Fullscreen
  const fullscreen = () => {
    const video = videoRef.current;

    if (!video) return;

    try {
      const request =
        video.requestFullscreen ||
        video.webkitRequestFullscreen ||
        video.webkitEnterFullscreen ||
        video.msRequestFullscreen;

      if (typeof request !== "function") return;

      const result = request.call(video);
      if (result && typeof result.catch === "function") {
        result.catch(() => {
          /* fullscreen denied — ignore */
        });
      }
    } catch {
      /* fullscreen unavailable — ignore */
    }
  };

  return (
    <div className="hero-video-wrapper">

      {/* LCP: the poster paints immediately while the mp4 streams, so the
          largest element is a preloaded image instead of a video frame
          that lands seconds later. Layout is untouched — same box, same
          object-fit as the video element itself. */}
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={heroPoster}
          type="image/webp"
          fetchPriority="high"
        />
      </Helmet>

      <video
        ref={videoRef}
        className="hero-video-player"
        autoPlay
        muted
        playsInline
        poster={heroPoster}
        preload="metadata"
        onEnded={onVideoEnd}
        onError={handleError}
        onTimeUpdate={handleProgress}

      >
        {/* CDN-hosted H.264 encodes with faststart: 1080p CRF 21 for wide /
            retina screens, 720p CRF 22 for small or data-saver clients. */}
        <source key={heroVideo} src={heroVideo} type="video/mp4" />
        {/* Fallback encode for browsers that cannot fetch the preferred source. */}
        <source
          key={heroVideo === HERO_1920 ? HERO_1280 : HERO_1920}
          src={heroVideo === HERO_1920 ? HERO_1280 : HERO_1920}
          type="video/mp4"
        />

        Your browser does not support HTML5 video.
      </video>

      <div className="hero-video-content" aria-label="Zetech University introduction">
        <h1>Kenya&apos;s Leading Technological &amp; Entrepreneurial University</h1>
        <p>
          Invent the future through innovation, entrepreneurship, research and
          industry-driven education at Zetech University.
        </p>
      </div>

      {/* Reel navigation: the same left/right arrows the slider uses, so the
          video is just another frame in the loop. Left steps back to the last
          slide, right jumps forward into the first slide. */}

      {onPrev && (
        <button
          type="button"
          className="hero-arrow1 hero-arrow-left1"
          onClick={onPrev}
          aria-label="Previous slide"
          title="Previous slide"
        >
          <FaArrowLeft />
        </button>
      )}

      {onNext && (
        <button
          type="button"
          className="hero-arrow1 hero-arrow-right1"
          onClick={onNext}
          aria-label="Next slide"
          title="Next slide"
        >
          <FaArrowRight />
        </button>
      )}

      {/* Video Controls */}

      <div className="hero-video-controls">

        <button
          type="button"
          className="hero-video-btn"
          onClick={togglePlay}
          aria-label={playing ? "Pause Video" : "Play Video"}
          title={playing ? "Pause Video" : "Play Video"}
        >
          {playing ? <FaPause /> : <FaPlay />}
        </button>

        <button
          type="button"
          className="hero-video-btn"
          onClick={toggleMute}
          aria-label={muted ? "Unmute Video" : "Mute Video"}
          title={muted ? "Unmute Video" : "Mute Video"}
        >
          {muted ? <FaVolumeMute /> : <FaVolumeUp />}
        </button>

        <button
          type="button"
          className="hero-video-btn"
          onClick={skipVideo}
          aria-label="Skip Video"
          title="Skip Video"
        >
          <FaForward />
        </button>

        <button
          type="button"
          className="hero-video-btn"
          onClick={fullscreen}
          aria-label="Fullscreen"
          title="Fullscreen"
        >
          <FaExpand />
        </button>

      </div>

      {/* Progress Bar */}

      <div className="hero-video-progress">

        <div
          className="hero-video-progress-fill"
          style={{
            width: `${progress}%`
          }}
        />

      </div>

    </div>
  );
}
